import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Loader2, Save, Sparkles, Upload, FileText, Trash2, ExternalLink } from "lucide-react";

interface Props {
  regionId: string;
  region: string;
  year: number;
  isGlobalAdmin: boolean;
}

interface FormState {
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  timezone: string;
  venue_name: string;
  venue_address: string;
  city: string;
  state: string;
  contact_phone: string;
  speakers: string; // comma separated for simple editing
  is_placeholder: boolean;
  title: string;
  theme: string;
  scripture: string;
  coordinator_name: string;
  coordinator_email: string;
  registration_url: string;
}

const empty: FormState = {
  start_date: "", end_date: "", start_time: "", end_time: "", timezone: "CST",
  venue_name: "", venue_address: "", city: "", state: "", contact_phone: "",
  speakers: "", is_placeholder: false,
  title: "", theme: "", scripture: "", coordinator_name: "", coordinator_email: "", registration_url: "",
};

interface HotelGuide {
  url: string | null;
  filename: string | null;
  size: number | null;
  uploaded_at: string | null;
}

const HOTEL_GUIDE_VIEW_URL = "/hotel-accommodation-guide";

export const LatestConferenceEditor: React.FC<Props> = ({ regionId, region, year, isGlobalAdmin }) => {
  const [form, setForm] = useState<FormState>(empty);
  const [header, setHeader] = useState({ eyebrow: "Latest Conference", title: `OFUESI Annual Regional Conferences ${year}`, subtitle: "Pick your region to view details and register." });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hotelGuide, setHotelGuide] = useState<HotelGuide>({ url: null, filename: null, size: null, uploaded_at: null });
  const [hotelUploading, setHotelUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data: conf } = await supabase
      .from("conferences")
      .select("location, contact_phone, conference_dates, facilitators")
      .eq("region_id", regionId)
      .eq("conference_name", `OFUESI Annual Regional Conference ${year}`)
      .maybeSingle();
    const { data: kit } = await supabase
      .from("conference_kit_events")
      .select("title, theme, scripture, venue_name, venue_address, city, state, coordinator_name, coordinator_email, coordinator_phone, registration_url, start_date, end_date, start_time, end_time, hotel_guide_url, hotel_guide_filename, hotel_guide_size, hotel_guide_uploaded_at")
      .eq("region_id", regionId)
      .eq("conference_year", year)
      .maybeSingle();
    const d = (conf as any)?.conference_dates?.[0] || {};
    const speakers = Array.isArray((conf as any)?.facilitators)
      ? (conf as any).facilitators.map((f: any) => typeof f === "string" ? f : f?.name).filter(Boolean).join(", ")
      : "";
    setForm({
      start_date: d.start_date || (kit as any)?.start_date || "",
      end_date: d.end_date || (kit as any)?.end_date || "",
      start_time: d.start_time || (kit as any)?.start_time || "",
      end_time: d.end_time || (kit as any)?.end_time || "",
      timezone: d.timezone || "CST",
      venue_name: (kit as any)?.venue_name || "",
      venue_address: (kit as any)?.venue_address || "",
      city: (kit as any)?.city || "",
      state: (kit as any)?.state || "",
      contact_phone: (kit as any)?.coordinator_phone || (conf as any)?.contact_phone || "",
      speakers,
      is_placeholder: d.is_placeholder === true,
      title: (kit as any)?.title || "",
      theme: (kit as any)?.theme || "",
      scripture: (kit as any)?.scripture || "",
      coordinator_name: (kit as any)?.coordinator_name || "",
      coordinator_email: (kit as any)?.coordinator_email || "",
      registration_url: (kit as any)?.registration_url || "",
    });
    setHotelGuide({
      url: (kit as any)?.hotel_guide_url || null,
      filename: (kit as any)?.hotel_guide_filename || null,
      size: (kit as any)?.hotel_guide_size ?? null,
      uploaded_at: (kit as any)?.hotel_guide_uploaded_at || null,
    });
    if (isGlobalAdmin) {
      const { data: hdr } = await supabase.from("app_settings").select("value").eq("key", "latest_conference_header").maybeSingle();
      if (hdr?.value) setHeader({ ...header, ...(hdr.value as any) });
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [regionId, year]);

  const save = async () => {
    if (!form.is_placeholder && (!form.start_date || !form.end_date)) {
      toast({ title: "Dates required", description: "Provide start & end dates, or mark this region as a placeholder.", variant: "destructive" });
      return;
    }
    setSaving(true);
    const speakers = form.speakers.split(",").map((s) => s.trim()).filter(Boolean).map((name) => ({ name }));
    const { error } = await (supabase as any).rpc("upsert_latest_conference_region", {
      p_region_id: regionId,
      p_year: year,
      p_start_date: form.start_date || null,
      p_end_date: form.end_date || null,
      p_start_time: form.start_time || null,
      p_end_time: form.end_time || null,
      p_timezone: form.timezone || null,
      p_venue_name: form.venue_name || null,
      p_venue_address: form.venue_address || null,
      p_city: form.city || null,
      p_state: form.state || null,
      p_contact_phone: form.contact_phone || null,
      p_speakers: speakers,
      p_is_placeholder: form.is_placeholder,
    });
    // Also write fields that aren't covered by the RPC directly to the kit event.
    const extraPatch: Record<string, any> = {
      title: form.title || null,
      theme: form.theme || null,
      scripture: form.scripture || null,
      coordinator_name: form.coordinator_name || null,
      coordinator_email: form.coordinator_email || null,
      registration_url: form.registration_url || null,
      updated_at: new Date().toISOString(),
    };
    const { error: kitErr } = await supabase
      .from("conference_kit_events")
      .update(extraPatch)
      .eq("region_id", regionId)
      .eq("conference_year", year);
    setSaving(false);
    if (error || kitErr) {
      toast({ title: "Save failed", description: (error || kitErr)?.message, variant: "destructive" });
      return;
    }
    try {
      const mod = await import("@/lib/conferencePrefetch");
      mod.invalidateConferenceHubCache?.();
    } catch {}
    toast({ title: "Conference page card updated successfully", description: `${region} ${year} saved at ${new Date().toLocaleString()}.` });
    load();
  };

  const saveHeader = async () => {
    const { error } = await supabase.from("app_settings")
      .upsert({ key: "latest_conference_header", value: header as any, updated_at: new Date().toISOString() }, { onConflict: "key" });
    if (error) toast({ title: "Header save failed", description: error.message, variant: "destructive" });
    else toast({ title: "Header updated" });
  };

  const slugRegion = region.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  const hotelStoragePath = `conference-documents/${slugRegion}-${year}/hotel-guide.pdf`;

  const uploadHotelGuide = async (file: File) => {
    if (file.type !== "application/pdf") {
      toast({ title: "PDF required", description: "Please upload a PDF file.", variant: "destructive" });
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast({ title: "File too large", description: "Max 25 MB.", variant: "destructive" });
      return;
    }
    setHotelUploading(true);
    try {
      const { error: upErr } = await supabase.storage
        .from("general-media")
        .upload(hotelStoragePath, file, { contentType: "application/pdf", upsert: true, cacheControl: "3600" });
      if (upErr) throw upErr;
      const url = `storage://general-media/${hotelStoragePath}`;
      const { data: { user } } = await supabase.auth.getUser();
      const { error: updErr } = await supabase
        .from("conference_kit_events")
        .update({
          hotel_guide_url: url,
          hotel_guide_filename: file.name,
          hotel_guide_size: file.size,
          hotel_guide_uploaded_at: new Date().toISOString(),
          hotel_guide_uploaded_by: user?.id || null,
        })
        .eq("region_id", regionId)
        .eq("conference_year", year);
      if (updErr) throw updErr;
      setHotelGuide({ url, filename: file.name, size: file.size, uploaded_at: new Date().toISOString() });
      try {
        const mod = await import("@/lib/conferencePrefetch");
        mod.invalidateConferenceHubCache?.();
      } catch {}
      toast({ title: "Hotel guide uploaded", description: "Public registration page updated." });
    } catch (e: any) {
      toast({ title: "Upload failed", description: e?.message || String(e), variant: "destructive" });
    } finally {
      setHotelUploading(false);
    }
  };

  const removeHotelGuide = async () => {
    if (!confirm("Remove the current Hotel Accommodation Guide?")) return;
    setHotelUploading(true);
    try {
      await supabase.storage.from("general-media").remove([hotelStoragePath]);
      const { error } = await supabase
        .from("conference_kit_events")
        .update({
          hotel_guide_url: null,
          hotel_guide_filename: null,
          hotel_guide_size: null,
          hotel_guide_uploaded_at: null,
          hotel_guide_uploaded_by: null,
        })
        .eq("region_id", regionId)
        .eq("conference_year", year);
      if (error) throw error;
      setHotelGuide({ url: null, filename: null, size: null, uploaded_at: null });
      try {
        const mod = await import("@/lib/conferencePrefetch");
        mod.invalidateConferenceHubCache?.();
      } catch {}
      toast({ title: "Hotel guide removed" });
    } catch (e: any) {
      toast({ title: "Remove failed", description: e?.message || String(e), variant: "destructive" });
    } finally {
      setHotelUploading(false);
    }
  };

  const formatBytes = (n: number | null) => {
    if (!n) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };

  if (loading) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-4">
      {isGlobalAdmin && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500" /> Latest Conference — Public Header</CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-3 gap-3">
            <div><Label className="text-xs">Eyebrow</Label><Input value={header.eyebrow} onChange={(e) => setHeader({ ...header, eyebrow: e.target.value })} /></div>
            <div><Label className="text-xs">Title</Label><Input value={header.title} onChange={(e) => setHeader({ ...header, title: e.target.value })} /></div>
            <div><Label className="text-xs">Subtitle</Label><Input value={header.subtitle} onChange={(e) => setHeader({ ...header, subtitle: e.target.value })} /></div>
            <div className="md:col-span-3"><Button size="sm" onClick={saveHeader}><Save className="w-4 h-4 mr-1" /> Save Header</Button></div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            {region} · {year}
            {form.is_placeholder && <Badge variant="outline" className="border-amber-400 text-amber-700">Placeholder</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-xs">
            Saved values feed the public Latest Conference cards and the Flyer Builder for this region.
            Flyer publish is blocked while <strong>Placeholder</strong> is on.
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="text-xs">Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><Label className="text-xs">End date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            <div><Label className="text-xs">Start time (24h, e.g. 18:00)</Label><Input value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} placeholder="18:00" /></div>
            <div><Label className="text-xs">End time</Label><Input value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} placeholder="14:00" /></div>
            <div>
              <Label className="text-xs">Timezone</Label>
              <Select value={form.timezone} onValueChange={(v) => setForm({ ...form, timezone: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["EST","CST","MST","PST","EDT","CDT","MDT","PDT"].map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Switch checked={form.is_placeholder} onCheckedChange={(v) => setForm({ ...form, is_placeholder: v })} id="ph" />
              <Label htmlFor="ph" className="text-xs">Mark as placeholder (dates tentative)</Label>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="text-xs">Venue name</Label><Input value={form.venue_name} onChange={(e) => setForm({ ...form, venue_name: e.target.value })} /></div>
            <div><Label className="text-xs">Venue address</Label><Input value={form.venue_address} onChange={(e) => setForm({ ...form, venue_address: e.target.value })} /></div>
            <div><Label className="text-xs">City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div><Label className="text-xs">State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
            <div><Label className="text-xs">Contact phone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></div>
            <div><Label className="text-xs">Speakers (comma-separated)</Label><Textarea value={form.speakers} onChange={(e) => setForm({ ...form, speakers: e.target.value })} rows={2} /></div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="text-xs">Conference title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={`OFUESI Annual Regional Conference ${year}`} /></div>
            <div><Label className="text-xs">Theme</Label><Input value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} placeholder="Breathe Again" /></div>
            <div className="md:col-span-2"><Label className="text-xs">Theme scripture</Label><Input value={form.scripture} onChange={(e) => setForm({ ...form, scripture: e.target.value })} placeholder="Ezekiel 37 — Valley of Dry Bones" /></div>
            <div><Label className="text-xs">Coordinator name</Label><Input value={form.coordinator_name} onChange={(e) => setForm({ ...form, coordinator_name: e.target.value })} /></div>
            <div><Label className="text-xs">Coordinator email</Label><Input type="email" value={form.coordinator_email} onChange={(e) => setForm({ ...form, coordinator_email: e.target.value })} /></div>
            <div className="md:col-span-2"><Label className="text-xs">Registration URL</Label><Input value={form.registration_url} onChange={(e) => setForm({ ...form, registration_url: e.target.value })} placeholder="https://…" /></div>
          </div>

          <div className="rounded-lg border border-sky-200 bg-sky-50/40 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-sky-700" />
              <h4 className="text-sm font-semibold text-sky-900">Hotel Accommodation Guide (PDF)</h4>
            </div>
            <p className="text-xs text-slate-600">
              Upload or replace the recommended hotels PDF shown on the public registration page when attendees pick <strong>Hotel Accommodation</strong>. Opens in a new browser tab; no code changes required.
            </p>

            {hotelGuide.url ? (
              <div className="rounded-md border border-sky-200 bg-white p-3 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <FileText className="w-5 h-5 text-sky-600 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{hotelGuide.filename || "hotel-guide.pdf"}</div>
                    <div className="text-xs text-slate-500">
                      {formatBytes(hotelGuide.size)}
                      {hotelGuide.uploaded_at ? ` · Uploaded ${new Date(hotelGuide.uploaded_at).toLocaleString()}` : ""}
                    </div>
                  </div>
                </div>
                <a
                  href={`${HOTEL_GUIDE_VIEW_URL}?regionId=${encodeURIComponent(regionId)}&year=${encodeURIComponent(String(year))}&documentType=hotel-guide`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-900 hover:underline"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open
                </a>
                <Button size="sm" variant="outline" onClick={removeHotelGuide} disabled={hotelUploading}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                </Button>
              </div>
            ) : (
              <div className="text-xs text-slate-500 italic">No hotel guide uploaded yet — public page will show “Hotel Accommodation Guide will be available soon.”</div>
            )}

            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="file"
                accept="application/pdf"
                className="hidden"
                disabled={hotelUploading}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadHotelGuide(f);
                  e.currentTarget.value = "";
                }}
              />
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white shadow-sm bg-gradient-to-r from-sky-600 to-amber-500 hover:opacity-90 transition">
                {hotelUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {hotelGuide.url ? "Replace PDF" : "Upload PDF"}
              </span>
            </label>
          </div>


          <div className="flex justify-end">
            <Button onClick={save} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Save {region}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LatestConferenceEditor;
